// O «ver como» do master (000335): o estado desta ABA e o filtro dos pedidos.
//
// Quem decide é o banco. O master abre uma sessão (`ver_como_iniciar`), e o
// token dela vai no cabeçalho `x-ver-como` de cada pedido à API; o gancho de
// pré-requisição põe o pedido em somente leitura e faz `meu_profile_id()`
// responder pela outra pessoa. Este módulo só faz três coisas:
//
//   1. guarda a sessão no sessionStorage — por ABA, de propósito: outra aba do
//      mesmo master continua sendo ele, e fechar a aba é sair do «ver como»;
//   2. põe o cabeçalho nos pedidos à API de dados (`/rest/v1`);
//   3. RECUSA, sem sair do navegador, o que levaria a uma conta de verdade: as
//      Edge Functions (o link mágico do Memberkit, o SSO do CFC, as IAs, a
//      emissão, a Receita), o upload no Storage e a troca de senha. Elas não
//      passam pelo gancho — rodam com a sessão do master —, então a recusa delas
//      só pode ser aqui. A da API de dados é do banco, e esta só a traduz.
//
// NÃO importa `./supabase`: o cliente importa este módulo para receber o
// `fetch`, e o caminho inverso faria um ciclo de import. Quem chama as RPCs do
// «ver como» é `lib/admin.ts` (abrir) e `BarraVerComo` (voltar).

const CHAVE = "cf:ver-como";
const CHAVE_DO_FIM = "cf:ver-como-fim";

export type SessaoVerComo = {
  /** O id da sessão em `ver_como_sessoes` — o token do cabeçalho. */
  token: string;
  alvo: { id: string; nome: string | null; email: string };
  expira_em: string;
  /** Para onde «Voltar ao normal» leva: a tela de onde o master saiu. */
  voltar_para: string;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ler(): SessaoVerComo | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    if (!bruto) return null;
    const s = JSON.parse(bruto) as Partial<SessaoVerComo>;
    // A forma é conferida porque o sessionStorage é do navegador: uma sessão
    // torta não pode virar cabeçalho, nem um `voltar_para` de fora do hub.
    if (
      typeof s.token !== "string" ||
      !UUID.test(s.token) ||
      typeof s.alvo?.id !== "string" ||
      typeof s.alvo?.email !== "string" ||
      typeof s.expira_em !== "string" ||
      Number.isNaN(Date.parse(s.expira_em)) ||
      typeof s.voltar_para !== "string" ||
      !s.voltar_para.startsWith("/") ||
      s.voltar_para.startsWith("//")
    ) {
      sessionStorage.removeItem(CHAVE);
      return null;
    }
    // Vencida já na abertura da página: o banco recusaria o primeiro pedido, e
    // a tela piscaria a vista da outra pessoa antes de sair.
    if (Date.parse(s.expira_em) <= Date.now()) {
      sessionStorage.removeItem(CHAVE);
      anotarFim("A visualização como outra pessoa terminou: ela vale uma hora.");
      return null;
    }
    return s as SessaoVerComo;
  } catch {
    return null;
  }
}

// Lido UMA vez, na carga do módulo. Entrar e sair do «ver como» recarregam a
// página inteira, e é isso que garante que nenhum dado em memória (o `useHub`,
// as leituras já feitas) atravesse de uma pessoa para a outra.
let ativa: SessaoVerComo | null = ler();

export function sessaoVerComo(): SessaoVerComo | null {
  return ativa;
}

/** Guarda a sessão que `ver_como_iniciar` abriu. Quem recarrega é quem chama. */
export function guardarSessaoVerComo(s: SessaoVerComo) {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(s));
  } catch {
    /* sem sessionStorage não há «ver como», e a página recarregada mostra isso */
  }
}

/** Esquece a sessão NESTA aba. A partir daqui, nenhum pedido leva o cabeçalho. */
export function esquecerSessaoVerComo() {
  ativa = null;
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    /* nada a apagar */
  }
}

function anotarFim(texto: string) {
  try {
    sessionStorage.setItem(CHAVE_DO_FIM, texto);
  } catch {
    /* o aviso é conforto */
  }
}

/** O aviso de que o «ver como» terminou sem o master pedir. Ler não apaga. */
export function lerAvisoDoFimDoVerComo(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DO_FIM);
  } catch {
    return null;
  }
}

export function apagarAvisoDoFimDoVerComo() {
  try {
    sessionStorage.removeItem(CHAVE_DO_FIM);
  } catch {
    /* nada a apagar */
  }
}

let saindo = false;
/**
 * Sai do «ver como» sem passar pelo banco: a sessão já não vale (venceu, foi
 * encerrada em outra aba, o master deixou de ser master). Recarrega na tela de
 * onde o master veio, com o aviso do porquê.
 */
export function sairDoVerComoSemAvisarOBanco(texto: string) {
  if (saindo) return;
  saindo = true;
  const destino = ativa?.voltar_para ?? "/admin/alunos";
  esquecerSessaoVerComo();
  anotarFim(texto);
  window.location.assign(destino);
}

/** A frase de cada recusa do gancho (000335) — a mensagem do erro é a chave. */
const FIM_POR_MOTIVO: Record<string, string> = {
  ver_como_expirou: "A visualização como outra pessoa terminou: ela vale uma hora.",
  ver_como_encerrado: "A visualização como outra pessoa foi encerrada (talvez em outra aba).",
  ver_como_sem_master: "A visualização terminou: a sua conta não é mais master.",
  ver_como_invalido: "A visualização terminou: o banco não reconheceu a sessão desta aba.",
};

export const MENSAGEM_SO_LEITURA =
  "Você está vendo o hub como outra pessoa, e nada é gravado assim. Volte ao normal para fazer isto com a sua conta.";

export const MENSAGEM_DE_FORA =
  "No «ver como», o que abre outro sistema (aulas, IA, emissão de documento, cálculo na Receita) fica desligado: ele entraria numa conta de verdade.";

// ---------------------------------------------------------------------------
// O filtro dos pedidos
// ---------------------------------------------------------------------------
const BASE = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "").replace(/\/+$/, "");
const fetchDaJanela: typeof fetch = globalThis.fetch.bind(globalThis);

/** Uma recusa com as chaves que os leitores da casa procuram (`message`, `motivo`, `erro`). */
function recusa(status: number, code: string, mensagem: string, motivo: string): Response {
  return new Response(
    JSON.stringify({ code, message: mensagem, details: null, hint: null, motivo, erro: mensagem, mensagem }),
    { status, headers: { "content-type": "application/json" } },
  );
}

function urlDe(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function metodoDe(input: RequestInfo | URL, init?: RequestInit): string {
  return (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
}

/**
 * O `fetch` do hub durante o «ver como». Fora dele (nenhuma sessão nesta aba),
 * é o `fetch` do navegador e nada mais.
 */
export async function fetchDoVerComo(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const s = ativa;
  const url = urlDe(input);
  if (!s || !BASE || !url.startsWith(BASE + "/")) return fetchDaJanela(input, init);

  const caminho = new URL(url).pathname;
  const metodo = metodoDe(input, init);
  const leitura = metodo === "GET" || metodo === "HEAD";

  if (caminho.startsWith("/functions/v1/")) {
    return recusa(403, "ver_como", MENSAGEM_DE_FORA, "ver-como");
  }
  if (caminho.startsWith("/storage/v1/")) {
    // Baixar e pedir o link assinado (que é um POST) são leitura; o resto
    // (enviar, apagar, mover) é escrita no bucket.
    if (leitura || /^\/storage\/v1\/object\/(sign|list)\//.test(caminho)) return fetchDaJanela(input, init);
    return recusa(403, "ver_como", MENSAGEM_SO_LEITURA, "ver-como");
  }
  if (caminho.startsWith("/auth/v1/")) {
    // Trocar a senha ou o e-mail mexeria na conta do MASTER, que é a sessão
    // desta aba — e a tela diz que é a de outra pessoa.
    if (caminho === "/auth/v1/user" && !leitura) return recusa(403, "ver_como", MENSAGEM_SO_LEITURA, "ver-como");
    const r = await fetchDaJanela(input, init);
    if (caminho === "/auth/v1/logout") esquecerSessaoVerComo();
    return r;
  }
  if (!caminho.startsWith("/rest/v1/")) return fetchDaJanela(input, init);

  // A API de dados: o banco recusa a escrita (o gancho), e a escrita direta em
  // tabela é recusada aqui também, sem viagem — PATCH, DELETE e o POST que não
  // é RPC não têm leitura possível.
  if (!leitura && !caminho.startsWith("/rest/v1/rpc/")) {
    return recusa(405, "25006", MENSAGEM_SO_LEITURA, "ver-como");
  }
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set("x-ver-como", s.token);
  const r = await fetchDaJanela(input instanceof Request ? new Request(input, { headers }) : input, { ...init, headers });
  if (r.ok) return r;

  let corpo: { code?: unknown; message?: unknown } | null = null;
  try {
    corpo = await r.clone().json();
  } catch {
    return r;
  }
  const mensagem = typeof corpo?.message === "string" ? corpo.message : "";
  if (r.status === 403 && mensagem in FIM_POR_MOTIVO) {
    sairDoVerComoSemAvisarOBanco(FIM_POR_MOTIVO[mensagem]);
    return r;
  }
  if (corpo?.code === "25006") {
    // "cannot execute INSERT in a read-only transaction" é verdade e não ajuda
    // ninguém: as telas mostram a mensagem do erro, e esta diz o que fazer.
    const novos = new Headers(r.headers);
    novos.delete("content-length");
    novos.delete("content-encoding");
    return new Response(JSON.stringify({ ...corpo, message: MENSAGEM_SO_LEITURA }), {
      status: r.status,
      statusText: r.statusText,
      headers: novos,
    });
  }
  return r;
}

/**
 * Liga o «ver como» nesta página, se a aba tem uma sessão. Chamado UMA vez,
 * antes de o cliente do Supabase nascer.
 *
 * O `fetch` da janela também é trocado, e não só o do cliente: duas telas falam
 * com Edge Functions por `fetch` cru, por causa do streaming (a conversa das IAs
 * e a do Leitor de Processos), e elas também precisam ser recusadas.
 */
export function instalarVerComo() {
  if (!ativa) return;
  window.fetch = fetchDoVerComo;
  document.documentElement.dataset.verComo = "";
}
